/**
 * Wizard Steg 1: Bokföringsdata
 *
 * Användaren väljer hur de vill importera sina bokföringsdata:
 * 1. Ladda upp SIE-fil (befintlig funktionalitet)
 * 2. Ange uppgifter manuellt (nytt)
 * 3. Koppla bokföringssystem (Fortnox, Spiris, etc.) (nytt - placeholder)
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Upload,
  FileText,
  AlertCircle,
  CheckCircle,
  Info,
  ShieldCheck,
  ShieldAlert,
  Keyboard,
  Link2,
  ArrowLeft,
  Sparkles,
  Clock,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { reportsApi, type AnnualReport, type ParsedSieData, type UserInput, type Company, type Integration } from "@/lib/api";
import { IntegrationSelector } from "@/components/integrations";
import { Spinner } from "@/components/ui/spinner";
import { K2EligibilityModal } from "@/components/wizard/K2EligibilityModal";
import { FiscalYearModal } from "@/components/wizard/FiscalYearModal";
import { createEmptyParsedSieData, isManualEntry } from "@/lib/manual-entry";

// Data source types
type DataSourceType = "sie" | "manual" | "integration";

interface Step1DataSourceProps {
  report: AnnualReport;
  onUpdate: (report: AnnualReport) => void;
  /** Override SIE upload (for guest/public wizard) */
  onUploadSie?: (file: File) => Promise<{ success: boolean; report?: AnnualReport; error?: string }>;
  /** Override save (for guest/public wizard) */
  onSaveInput?: (data: Record<string, unknown>) => Promise<{ success: boolean; report?: AnnualReport; error?: string }>;
  /** Override navigation (for guest/public wizard or K3 decimal steps) */
  onNavigateStep?: (step: number | string) => void;
  /** Next step after successful upload (default: 2, guest mode: 3) */
  nextStep?: number | string;
  /** Guest org.nr for validation when syncing from accounting system */
  guestOrgNumber?: string;
}

export function Step1DataSource({
  report,
  onUpdate,
  onUploadSie,
  onSaveInput,
  onNavigateStep,
  nextStep = 2,
  guestOrgNumber,
}: Step1DataSourceProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // View state: null = selection view, or specific data source
  // Initialize from URL param to avoid race conditions with useEffect
  const [selectedSource, setSelectedSource] = useState<DataSourceType | null>(() => {
    // Check URL for source param at initialization time (before first render)
    const urlParams = new URLSearchParams(window.location.search);
    const sourceParam = urlParams.get("source");
    if (sourceParam === "integration" || sourceParam === "sie" || sourceParam === "manual") {
      return sourceParam as DataSourceType;
    }
    return null;
  });

  // Track if we've processed the source query param (prevents race condition with setSearchParams)
  // Initialize to true if URL already has a source param (handled by useState initializer above)
  const sourceParamProcessedRef = useRef<boolean>(
    (() => {
      const urlParams = new URLSearchParams(window.location.search);
      const sourceParam = urlParams.get("source");
      return sourceParam === "integration" || sourceParam === "sie" || sourceParam === "manual";
    })()
  );

  // Track if we just returned from OAuth (to trigger refresh in IntegrationSelector)
  const [justReturnedFromOAuth, setJustReturnedFromOAuth] = useState<boolean>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("source") === "integration";
  });

  // Track if we've done the initial auto-selection (prevents re-selecting when user clicks back)
  const initialAutoSelectDoneRef = useRef<boolean>(false);

  // SIE upload states
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // K2 2026 eligibility modal state
  const [showK2Modal, setShowK2Modal] = useState(false);
  const [pendingReport, setPendingReport] = useState<AnnualReport | null>(null);

  // Manual entry fiscal year modal state
  const [showFiscalYearModal, setShowFiscalYearModal] = useState(false);

  // Current report state
  const sieData = report.parsedSieData as ParsedSieData | null;
  const userInput = report.userInput as UserInput;

  // Check if fiscal year starts 2026-01-01 or later
  const fiscalYearStart = sieData?.metadata?.fiscalYearStart || "";
  const isFiscalYear2026OrLater = (() => {
    if (!fiscalYearStart) return false;
    const startDate = new Date(fiscalYearStart);
    const cutoffDate = new Date("2026-01-01");
    return startDate >= cutoffDate;
  })();

  // K2 eligibility status
  const k2EligibilityConfirmed = !!userInput?.k2Eligibility?.confirmedAt;
  const k2EligibilityPasses =
    !userInput?.k2Eligibility?.hasForeignBranches &&
    !userInput?.k2Eligibility?.hasCryptoAssets &&
    !userInput?.k2Eligibility?.hasShareBasedPayments;

  // Helper to check if fiscal year requires K2 2026 eligibility confirmation
  const checkRequiresK2Confirmation = (newReport: AnnualReport): boolean => {
    const newSieData = newReport.parsedSieData as ParsedSieData | null;
    const newUserInput = newReport.userInput as UserInput;

    if (!newSieData?.metadata?.fiscalYearStart) return false;

    const startDate = new Date(newSieData.metadata.fiscalYearStart);
    const cutoffDate = new Date("2026-01-01");
    const is2026OrLater = startDate >= cutoffDate;

    const alreadyConfirmed = !!newUserInput?.k2Eligibility?.confirmedAt;

    return is2026OrLater && !alreadyConfirmed;
  };

  // Check if this is a guest session (no real companyId)
  const isGuest = !report.companyId || report.companyId === "guest";

  // Handle source query param (e.g., after OAuth redirect)
  // Note: selectedSource is already initialized from URL params above, so this effect
  // just handles clearing the URL and ensuring state is set (for navigation scenarios)
  useEffect(() => {
    const sourceParam = searchParams.get("source");
    if (sourceParam === "integration" || sourceParam === "sie" || sourceParam === "manual") {
      // Mark as processed
      sourceParamProcessedRef.current = true;
      // Ensure state is set (handles cases where component navigated to, not just mounted)
      setSelectedSource(sourceParam as DataSourceType);
      // Mark that we just returned from OAuth (for triggering refresh in IntegrationSelector)
      if (sourceParam === "integration") {
        setJustReturnedFromOAuth(true);
      }
    }
  }, [searchParams]);

  // Separate effect to clear the source param from URL AFTER state is set
  // This prevents the URL from being cleared before the state update is applied
  useEffect(() => {
    // Only clear if we have a selected source and the URL still has the source param
    if (selectedSource && searchParams.get("source")) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("source");
      setSearchParams(newParams, { replace: true });
    }
  }, [selectedSource, searchParams, setSearchParams]);

  // Auto-set selectedSource based on existing data or guest session (ONLY on initial mount)
  // This runs once to restore state based on existing data, but doesn't re-run when user clicks back
  useEffect(() => {
    // Only run this once - if we've already done initial selection, don't override user's choice
    if (initialAutoSelectDoneRef.current) return;

    // Skip if source was already set from query param OR if source param exists in URL
    // (the other useEffect will handle it)
    // Also check the ref to handle the race condition where setSearchParams triggers a re-render
    // before setSelectedSource state update is applied
    if (selectedSource !== null) {
      initialAutoSelectDoneRef.current = true;
      return;
    }
    if (sourceParamProcessedRef.current) {
      initialAutoSelectDoneRef.current = true;
      return;
    }
    const sourceParam = searchParams.get("source");
    if (sourceParam === "integration" || sourceParam === "sie" || sourceParam === "manual") {
      initialAutoSelectDoneRef.current = true;
      return;
    }

    // First, check for guest integration session in sessionStorage
    if (isGuest) {
      const guestSessionKey = sessionStorage.getItem("guest_integration_session");
      const guestProvider = sessionStorage.getItem("guest_integration_provider");

      if (guestSessionKey && guestProvider) {
        // Guest has an active integration session - show integration view
        setSelectedSource("integration");
        initialAutoSelectDoneRef.current = true;
        return;
      }
    }

    // Then check for existing SIE data
    if (sieData) {
      // Check if data is from manual entry
      if (isManualEntry(sieData)) {
        setSelectedSource("manual");
        // Show K2 modal if needed for manual entry
        if (isFiscalYear2026OrLater && !k2EligibilityConfirmed) {
          setShowK2Modal(true);
        }
      } else {
        // Regular SIE data
        setSelectedSource("sie");
        if (isFiscalYear2026OrLater && !k2EligibilityConfirmed) {
          setShowK2Modal(true);
        }
      }
      initialAutoSelectDoneRef.current = true;
    }
  }, [sieData, isFiscalYear2026OrLater, k2EligibilityConfirmed, isGuest, selectedSource, searchParams]);

  // ════════════════════════════════════════════════════════════════════
  // SIE Upload Handlers (existing functionality)
  // ════════════════════════════════════════════════════════════════════

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  }, []);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  }, []);

  const handleFile = async (file: File) => {
    // Validate file extension
    const ext = file.name.toLowerCase().split(".").pop();
    if (!["se", "si", "sie"].includes(ext || "")) {
      setError("Filen måste vara en SIE-fil (.se, .si eller .sie)");
      return;
    }

    setUploadedFile(file);
    setError(null);
    setIsUploading(true);

    try {
      if (onUploadSie) {
        // Guest/public mode: use provided upload handler
        const result = await onUploadSie(file);
        if (result.success && result.report) {
          onUpdate(result.report);

          // Check if K2 eligibility confirmation is required
          if (checkRequiresK2Confirmation(result.report)) {
            setPendingReport(result.report);
            setShowK2Modal(true);
          } else {
            if (onNavigateStep) onNavigateStep(nextStep);
            else navigate(`/dashboard/wizard/${report.id}/step/${nextStep}`);
          }
        } else {
          setError(result.error || "Kunde inte ladda upp filen");
        }
      } else {
        // Authenticated mode: use API
        const response = await reportsApi.uploadSie(report.id, file);
        if (response.success && response.data) {
          onUpdate(response.data.report);

          // Check if K2 eligibility confirmation is required
          if (checkRequiresK2Confirmation(response.data.report)) {
            setPendingReport(response.data.report);
            setShowK2Modal(true);
          } else {
            navigate(`/dashboard/wizard/${report.id}/step/2`);
          }
        } else {
          setError(response.error?.message || "Kunde inte ladda upp filen");
        }
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Ett fel uppstod vid uppladdning");
      setError("Ett fel uppstod vid uppladdning");
    } finally {
      setIsUploading(false);
    }
  };

  // Handle K2 eligibility confirmed from modal
  const handleK2Confirmed = (passes: boolean) => {
    setShowK2Modal(false);
    setPendingReport(null);

    if (passes) {
      // Navigate to next step
      if (onNavigateStep) onNavigateStep(nextStep);
      else navigate(`/dashboard/wizard/${report.id}/step/${nextStep}`);
    }
    // If not passes, stay on page - user will see the K2 failure status
  };

  // Navigation to next step (for already uploaded files)
  const handleContinue = () => {
    if (onNavigateStep) onNavigateStep(nextStep);
    else navigate(`/dashboard/wizard/${report.id}/step/${nextStep}`);
  };

  // ════════════════════════════════════════════════════════════════════
  // Manual Entry Handlers
  // ════════════════════════════════════════════════════════════════════

  // Handle manual entry - show fiscal year modal
  const handleManualEntryClick = () => {
    setShowFiscalYearModal(true);
  };

  // Handle fiscal year confirmed from modal
  const handleFiscalYearConfirmed = async (
    fiscalYearStart: string,
    fiscalYearEnd: string
  ) => {
    // Get company info from report (for logged-in users)
    const company = report.company as Company | null;
    const companyName = company?.name || "";
    const organizationNumber = company?.organizationNumber || "";

    // Create empty ParsedSieData structure for manual entry
    const emptyParsedSieData = createEmptyParsedSieData(
      fiscalYearStart,
      fiscalYearEnd,
      companyName,
      organizationNumber
    );

    // Save to report (use custom handler for guest mode or API for logged-in)
    if (onSaveInput) {
      // Guest mode - save via localStorage handler
      const result = await onSaveInput({
        ...report.userInput,
        manualEntry: {
          fiscalYearStart,
          fiscalYearEnd,
          createdAt: new Date().toISOString(),
        },
      });

      if (result.success && result.report) {
        // Check if K2 confirmation is required BEFORE updating
        const requiresK2 = checkRequiresK2Confirmation({
          ...result.report,
          parsedSieData: emptyParsedSieData,
        });

        // Update the report with the empty parsed data and next step
        // Include currentStep so handleReportUpdate in PublicWizardPage navigates
        // Convert nextStep to number for AnnualReport.currentStep (always a number)
        const nextStepNum = typeof nextStep === "string" ? parseFloat(nextStep) : nextStep;
        const updatedReport = {
          ...result.report,
          parsedSieData: emptyParsedSieData,
          currentStep: requiresK2 ? (result.report.currentStep || 2) : nextStepNum,
        };
        onUpdate(updatedReport);

        // If K2 confirmation is required, show modal (navigation is paused)
        if (requiresK2) {
          setPendingReport(updatedReport);
          setShowK2Modal(true);
        }
        // Otherwise, navigation happens via onUpdate -> handleReportUpdate in PublicWizardPage
      } else {
        toast.error(result.error || "Kunde inte skapa manuell rapport");
      }
    } else {
      // Logged-in mode - use API
      try {
        // First, save the manual entry metadata
        const updateResponse = await reportsApi.updateInput(report.id, {
          ...report.userInput,
          manualEntry: {
            fiscalYearStart,
            fiscalYearEnd,
            createdAt: new Date().toISOString(),
          },
        });

        if (updateResponse.success && updateResponse.data) {
          // Also save the empty parsedSieData
          const sieResponse = await reportsApi.saveManualSieData(
            report.id,
            emptyParsedSieData
          );

          if (sieResponse.success && sieResponse.data) {
            onUpdate(sieResponse.data.report);

            // Check if K2 confirmation is required
            if (checkRequiresK2Confirmation(sieResponse.data.report)) {
              setPendingReport(sieResponse.data.report);
              setShowK2Modal(true);
            } else {
              // Navigate to next step
              navigate(`/dashboard/wizard/${report.id}/step/${nextStep}`);
            }
          } else {
            toast.error("Kunde inte spara bokföringsdata");
          }
        } else {
          toast.error("Kunde inte skapa manuell rapport");
        }
      } catch (err) {
        console.error("Manual entry save error:", err);
        toast.error("Ett fel uppstod");
      }
    }
  };

  // ════════════════════════════════════════════════════════════════════
  // Data Source Selection View
  // ════════════════════════════════════════════════════════════════════

  if (selectedSource === null) {
    return (
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Hämta bokföringsdata
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Välj hur du vill importera företagets bokföring. Vi stödjer flera olika sätt att få in data
            för att skapa din årsredovisning.
          </p>
        </div>

        {/* Data Source Cards */}
        <div className="grid gap-6 md:grid-cols-3">
          {/* SIE File Upload */}
          <button
            onClick={() => setSelectedSource("sie")}
            className="group relative flex flex-col text-left bg-white rounded-2xl border-2 border-gray-200 p-6 transition-all duration-200 hover:border-primary-500 hover:shadow-lg hover:shadow-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            {/* Popular badge */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-primary-100 text-primary-700 border border-primary-200">
                <Sparkles size={12} />
                Populärast
              </span>
            </div>

            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200">
              <FileText className="text-white" size={28} />
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              SIE-fil
            </h3>

            <p className="text-sm text-gray-600 mb-4 flex-1">
              Exportera en SIE4-fil från ditt bokföringsprogram och ladda upp den här.
              Fungerar med alla svenska bokföringsprogram.
            </p>

            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Clock size={14} />
              <span>Ca 2 minuter</span>
            </div>
          </button>

          {/* Automatic Integration */}
          <button
            onClick={() => setSelectedSource("integration")}
            className="group relative flex flex-col text-left bg-white rounded-2xl border-2 border-gray-200 p-6 transition-all duration-200 hover:border-emerald-500 hover:shadow-lg hover:shadow-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
          >
            {/* Beta badge */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                <Zap size={12} />
                Automatiskt
              </span>
            </div>

            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200">
              <Link2 className="text-white" size={28} />
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Koppla bokföringssystem
            </h3>

            <p className="text-sm text-gray-600 mb-4 flex-1">
              Anslut direkt till Fortnox, Spiris, Björn Lundén eller Bokio.
              Vi hämtar bokföringen automatiskt.
            </p>

            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Zap size={14} />
              <span>Automatisk synkronisering</span>
            </div>
          </button>

          {/* Manual Entry */}
          <button
            onClick={() => setSelectedSource("manual")}
            className="group relative flex flex-col text-left bg-white rounded-2xl border-2 border-gray-200 p-6 transition-all duration-200 hover:border-amber-500 hover:shadow-lg hover:shadow-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
          >
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200">
              <Keyboard className="text-white" size={28} />
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Ange manuellt
            </h3>

            <p className="text-sm text-gray-600 mb-4 flex-1">
              Fyll i resultat- och balansräkning manuellt.
              Bra för mindre bolag eller om du saknar SIE-fil.
            </p>

            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Clock size={14} />
              <span>Ca 15-30 minuter</span>
            </div>
          </button>
        </div>

        {/* Integration providers preview */}
        <div className="mt-10 text-center">
          <p className="text-xs text-gray-500 mb-4">Stödda bokföringssystem</p>
          <div className="flex items-center justify-center gap-6 sm:gap-8">
            <div className="flex flex-col items-center gap-1">
              <div className="w-10 h-10 bg-white rounded-lg border border-gray-200 flex items-center justify-center p-2">
                <img src="/logos/integrations/fortnox.svg" alt="Fortnox" className="w-full h-full object-contain" />
              </div>
              <span className="text-xs text-gray-500">Fortnox</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <div className="w-10 h-10 bg-white rounded-lg border border-gray-200 flex items-center justify-center p-2">
                <img src="/logos/integrations/spiris.svg" alt="Spiris" className="w-full h-full object-contain" />
              </div>
              <span className="text-xs text-gray-500">Spiris</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center p-2">
                <img src="/logos/integrations/bjorn-lunden.svg" alt="Björn Lundén" className="w-full h-full object-contain" />
              </div>
              <span className="text-xs text-gray-500">Björn Lundén</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <div className="w-10 h-10 bg-white rounded-lg border border-gray-200 flex items-center justify-center p-2">
                <img src="/logos/integrations/bokio.png" alt="Bokio" className="w-full h-full object-contain" />
              </div>
              <span className="text-xs text-gray-500">Bokio</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════
  // SIE Upload View
  // ════════════════════════════════════════════════════════════════════

  if (selectedSource === "sie") {
    return (
      <div className="max-w-2xl mx-auto">
        {/* Back button */}
        {!sieData && (
          <button
            onClick={() => setSelectedSource(null)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft size={18} />
            <span className="text-sm font-medium">Tillbaka till val av datakälla</span>
          </button>
        )}

        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Ladda upp SIE-fil
          </h2>
          <p className="text-gray-600">
            Exportera en SIE4-fil från ditt bokföringsprogram och ladda upp den här.
          </p>
        </div>

        {/* Info box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <Info className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-2">Så exporterar du SIE4:</p>
              <ul className="space-y-2 text-blue-700">
                <li className="flex items-center gap-2">
                  <img src="/logos/integrations/fortnox.svg" alt="" className="w-5 h-5 object-contain" />
                  <span><strong>Fortnox:</strong> Inställningar → Importera/Exportera → SIE</span>
                </li>
                <li className="flex items-center gap-2">
                  <img src="/logos/integrations/spiris.svg" alt="" className="w-5 h-5 object-contain" />
                  <span><strong>Spiris:</strong> Arkiv → Export → SIE-export</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 bg-slate-700 rounded flex items-center justify-center">
                    <img src="/logos/integrations/bjorn-lunden.svg" alt="" className="w-4 h-4 object-contain" />
                  </div>
                  <span><strong>Björn Lundén:</strong> Arkiv → Skicka/ta emot → Export SIE</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
            <AlertCircle className="text-red-600 mt-0.5 flex-shrink-0" size={20} />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Upload area */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-all ${
            isDragging
              ? "border-primary-500 bg-primary-50"
              : "border-gray-300 hover:border-gray-400"
          } ${isUploading ? "pointer-events-none opacity-50" : ""}`}
        >
          <input
            type="file"
            accept=".se,.si,.sie"
            onChange={handleFileInput}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={isUploading}
          />

          {isUploading ? (
            <div className="flex flex-col items-center">
              <Spinner size="lg" />
              <p className="mt-4 text-gray-600">Analyserar SIE-fil...</p>
              {uploadedFile && (
                <p className="mt-2 text-sm text-gray-500">{uploadedFile.name}</p>
              )}
            </div>
          ) : uploadedFile ? (
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="text-green-600" size={32} />
              </div>
              <p className="text-lg font-medium text-gray-900 mb-1">
                {uploadedFile.name}
              </p>
              <p className="text-sm text-gray-500">
                Klicka eller dra för att byta fil
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Upload className="text-gray-400" size={32} />
              </div>
              <p className="text-lg font-medium text-gray-900 mb-1">
                Dra och släpp din SIE-fil här
              </p>
              <p className="text-sm text-gray-500">
                eller klicka för att välja fil
              </p>
              <p className="mt-4 text-xs text-gray-400">
                Stödjer SIE4 och SIE4E (.se, .si, .sie)
              </p>
            </div>
          )}
        </div>

        {/* Already has SIE data - show status and continue button */}
        {report.parsedSieData && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <CheckCircle className="text-green-600 mt-0.5 flex-shrink-0" size={20} />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800">
                  SIE-fil redan uppladdad
                </p>
                <p className="text-sm text-green-700 mt-1">
                  Du kan fortsätta till nästa steg eller ladda upp en ny fil.
                </p>

                {/* K2 2026 status for already confirmed */}
                {isFiscalYear2026OrLater && k2EligibilityConfirmed && (
                  <div
                    className={`mt-3 p-2 rounded-lg flex items-center gap-2 ${
                      k2EligibilityPasses ? "bg-green-100" : "bg-red-100"
                    }`}
                  >
                    {k2EligibilityPasses ? (
                      <>
                        <ShieldCheck size={16} className="text-green-600" />
                        <span className="text-sm text-green-800">K2-behörighet bekräftad</span>
                      </>
                    ) : (
                      <>
                        <ShieldAlert size={16} className="text-red-600" />
                        <span className="text-sm text-red-800">
                          Företaget uppfyller INTE K2-kraven
                        </span>
                      </>
                    )}
                  </div>
                )}

                {/* Show continue button only if K2 is not required OR already confirmed and passes */}
                {(!isFiscalYear2026OrLater || (k2EligibilityConfirmed && k2EligibilityPasses)) && (
                  <button onClick={handleContinue} className="mt-3 btn btn-primary">
                    Fortsätt till översikt
                  </button>
                )}

                {/* If K2 required but not confirmed, show button to open modal */}
                {isFiscalYear2026OrLater && !k2EligibilityConfirmed && (
                  <button
                    onClick={() => setShowK2Modal(true)}
                    className="mt-3 btn btn-primary flex items-center gap-2"
                  >
                    <ShieldCheck size={16} />
                    Bekräfta K2-behörighet
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* K2 2026 Eligibility - Failure status (shown when confirmed but doesn't pass) */}
        {sieData && isFiscalYear2026OrLater && k2EligibilityConfirmed && !k2EligibilityPasses && (
          <div className="mt-6 rounded-lg p-4 bg-red-50 border border-red-200">
            <div className="flex items-start gap-3">
              <ShieldAlert size={24} className="text-red-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-800">Företaget uppfyller INTE K2-kraven</p>
                <p className="text-sm text-red-600 mt-1">
                  Företag med filialer i utlandet, kryptotillgångar eller aktierelaterade
                  ersättningar får inte tillämpa K2 för räkenskapsår som börjar 2026-01-01 eller
                  senare.
                </p>
                <p className="text-xs text-red-500 mt-2">Referens: BFNAR 2016:10 punkt 1.1A</p>
              </div>
            </div>
          </div>
        )}

        {/* Supported formats */}
        <div className="mt-8 grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
            <FileText className="mx-auto text-gray-400 mb-2" size={24} />
            <p className="text-sm font-medium text-gray-900">SIE4</p>
            <p className="text-xs text-gray-500">Komplett export</p>
          </div>
          <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
            <FileText className="mx-auto text-gray-400 mb-2" size={24} />
            <p className="text-sm font-medium text-gray-900">SIE4E</p>
            <p className="text-xs text-gray-500">Med verifikationer</p>
          </div>
          <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
            <CheckCircle className="mx-auto text-green-500 mb-2" size={24} />
            <p className="text-sm font-medium text-gray-900">Auto-encoding</p>
            <p className="text-xs text-gray-500">CP437, ISO-8859-1, UTF-8</p>
          </div>
        </div>

        {/* K2 Eligibility Modal */}
        <K2EligibilityModal
          isOpen={showK2Modal}
          report={pendingReport || report}
          onUpdate={onUpdate}
          onConfirmed={handleK2Confirmed}
          onSaveInput={onSaveInput}
        />
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════
  // Integration View
  // ════════════════════════════════════════════════════════════════════

  // Handle integration sync complete
  const handleIntegrationSyncComplete = async (data: unknown) => {
    console.log("[DEBUG] handleIntegrationSyncComplete called with:", data);

    // Parse sync response data - includes data (ParsedSieData) and calculations
    const syncData = data as {
      type?: string;
      data?: ParsedSieData;
      calculations?: { keyFigures: ParsedSieData["keyFigures"]; triggeredNotes: string[] };
      financialYear?: { FromDate: string; ToDate: string };
    };

    console.log("[DEBUG] syncData.type:", syncData?.type);
    console.log("[DEBUG] syncData.data exists:", !!syncData?.data);
    console.log("[DEBUG] isGuest:", isGuest);
    console.log("[DEBUG] nextStep:", nextStep);

    // If we got SIE data, update report and navigate
    if (syncData?.type === "sie" && syncData?.data) {
      console.log("[DEBUG] SIE data detected, preparing to update report");

      const nextStepNum = typeof nextStep === "string" ? parseFloat(nextStep) : nextStep;

      // For logged-in users (not guest mode), we need to persist to database
      if (!isGuest) {
        console.log("[DEBUG] Logged-in user - saving parsedSieData to database");
        try {
          // Save the synced SIE data to database via API
          const sieResponse = await reportsApi.saveManualSieData(
            report.id,
            syncData.data
          );

          if (sieResponse.success && sieResponse.data) {
            console.log("[DEBUG] parsedSieData saved to database successfully");
            // Update local state with the saved report
            onUpdate(sieResponse.data.report);

            // Navigate to next step
            if (onNavigateStep) {
              onNavigateStep(nextStep);
            } else {
              navigate(`/dashboard/wizard/${report.id}/step/${nextStep}`);
            }
          } else {
            console.error("[DEBUG] Failed to save parsedSieData:", sieResponse.error);
            toast.error("Kunde inte spara bokföringsdata");
          }
        } catch (err) {
          console.error("[DEBUG] Error saving parsedSieData:", err);
          toast.error("Ett fel uppstod vid sparande av bokföringsdata");
        }
      } else {
        // For guest mode, update the report with the synced SIE data
        // The PublicWizardPage's handleReportUpdate will:
        // 1. Save parsedSieData to guestState and localStorage
        // 2. Update currentStep state (which triggers navigation via re-render)
        // So we DON'T need to call onNavigateStep separately for guests
        const updatedReport: AnnualReport = {
          ...report,
          parsedSieData: syncData.data,
          calculations: syncData.calculations || report.calculations,
          currentStep: nextStepNum,
        };
        console.log("[DEBUG] Guest mode - calling onUpdate with updatedReport.currentStep:", updatedReport.currentStep);
        console.log("[DEBUG] updatedReport.parsedSieData exists:", !!updatedReport.parsedSieData);
        onUpdate(updatedReport);
        console.log("[DEBUG] onUpdate called successfully for guest");
        // For guests: handleReportUpdate in PublicWizardPage handles navigation via setCurrentStep
      }
    } else {
      // Even without SIE data, if sync was successful, navigate
      toast.success("Bokföringsdata hämtad!");
      if (onNavigateStep) {
        onNavigateStep(nextStep);
      } else {
        navigate(`/dashboard/wizard/${report.id}/step/${nextStep}`);
      }
    }
  };

  // Handle integration connected
  const handleIntegrationConnected = (integration: Integration) => {
    toast.success(`Ansluten till ${integration.provider}!`);
  };

  if (selectedSource === "integration") {
    // CompanyId is used for integrations - for guests we use "guest" as a placeholder
    // The integration system handles guest mode by storing OAuth state differently
    const companyId = report.companyId || "guest";

    return (
      <div className="max-w-2xl mx-auto">
        {/* Back button */}
        <button
          onClick={() => setSelectedSource(null)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Tillbaka till val av datakälla</span>
        </button>

        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Koppla bokföringssystem
          </h2>
          <p className="text-gray-600">
            Anslut ditt bokföringsprogram för att automatiskt importera bokföringsdata.
          </p>
        </div>

        {/* Integration Selector Component */}
        <IntegrationSelector
          companyId={companyId}
          onConnected={handleIntegrationConnected}
          onSyncComplete={handleIntegrationSyncComplete}
          forceRefresh={justReturnedFromOAuth}
          guestOrgNumber={guestOrgNumber}
        />

        {/* Info about integration */}
        <div className="mt-8 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Info className="text-emerald-600 mt-0.5 flex-shrink-0" size={20} />
            <div className="text-sm text-emerald-800">
              <p className="font-medium mb-1">Hur fungerar kopplingen?</p>
              <p className="text-emerald-700">
                När du ansluter ditt bokföringsprogram ger du Kvanten tillåtelse att läsa din
                bokföringsdata. Du kan när som helst koppla bort anslutningen. All data överförs
                krypterat och vi sparar endast det som behövs för årsredovisningen.
              </p>
            </div>
          </div>
        </div>

        {/* Alternative */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500 mb-3">
            Föredrar du att ladda upp manuellt?
          </p>
          <button
            onClick={() => setSelectedSource("sie")}
            className="text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            Ladda upp en SIE-fil istället →
          </button>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════
  // Manual Entry View
  // ════════════════════════════════════════════════════════════════════

  // Check if report already has manual entry data
  const hasManualData = isManualEntry(sieData);

  if (selectedSource === "manual") {
    return (
      <div className="max-w-2xl mx-auto">
        {/* Back button */}
        <button
          onClick={() => setSelectedSource(null)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Tillbaka till val av datakälla</span>
        </button>

        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Ange uppgifter manuellt
          </h2>
          <p className="text-gray-600">
            Fyll i resultat- och balansräkning för hand. Bra för mindre bolag.
          </p>
        </div>

        {/* Already has manual data - show status */}
        {hasManualData && sieData ? (
          <div className="bg-green-50 border border-green-200 rounded-xl p-6">
            <div className="flex items-start space-x-3">
              <CheckCircle className="text-green-600 mt-0.5 flex-shrink-0" size={20} />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800">
                  Manuell inmatning påbörjad
                </p>
                <p className="text-sm text-green-700 mt-1">
                  Räkenskapsår: {sieData.metadata.fiscalYearStart} – {sieData.metadata.fiscalYearEnd}
                </p>
                <p className="text-sm text-green-600 mt-2">
                  Du kan fortsätta redigera i nästa steg eller börja om med nytt räkenskapsår.
                </p>
                <div className="flex gap-3 mt-4">
                  <button onClick={handleContinue} className="btn btn-primary">
                    Fortsätt till översikt
                  </button>
                  <button
                    onClick={handleManualEntryClick}
                    className="btn btn-secondary"
                  >
                    Ändra räkenskapsår
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Start manual entry */}
            <div className="bg-white border-2 border-amber-200 rounded-xl p-8 text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Keyboard className="text-amber-600" size={32} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Börja med att ange räkenskapsår
              </h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Ange start- och slutdatum för räkenskapsåret. Sedan kan du fylla i
                resultat- och balansräkning i nästa steg.
              </p>
              <button
                onClick={handleManualEntryClick}
                className="btn btn-primary"
              >
                Ange räkenskapsår
              </button>
            </div>

            {/* What you can enter */}
            <div className="mt-8">
              <h4 className="text-sm font-medium text-gray-900 mb-4">
                I nästa steg kan du ange:
              </h4>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                  <CheckCircle className="text-green-500 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Resultaträkning (K2-format)</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                  <CheckCircle className="text-green-500 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Balansräkning</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                  <CheckCircle className="text-green-500 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Jämförelsetal (flerårsöversikt)</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                  <CheckCircle className="text-green-500 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Notuppgifter</span>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Fiscal Year Modal */}
        <FiscalYearModal
          isOpen={showFiscalYearModal}
          onClose={() => setShowFiscalYearModal(false)}
          onConfirm={handleFiscalYearConfirmed}
          initialStartDate={sieData?.metadata?.fiscalYearStart}
          initialEndDate={sieData?.metadata?.fiscalYearEnd}
        />

        {/* K2 Eligibility Modal */}
        <K2EligibilityModal
          isOpen={showK2Modal}
          report={pendingReport || report}
          onUpdate={onUpdate}
          onConfirmed={handleK2Confirmed}
          onSaveInput={onSaveInput}
        />
      </div>
    );
  }

  return null;
}
